import { Injectable } from '@angular/core';
import { Cache, cacheable, BlobSerializer } from '../cache/index';
import { sevenDaysMilli } from '../constants-types/constants';
import {
    AssessmentType,
    LegacyGender,
    SortType,
    SortDirection,
    BulkUploadAssessmentType,
    SurveyUserTypes
} from '../constants-types/Enums';
import { InvitationSource } from '../core-data-model/Assessment.model';
import {
    BehavioralAssessmentServer,
    CognitiveAssessmentServer,
    GetAllAssessmentsServer,
    CompleteBehavioralAssessmentServer,
    PrintBehavioralReportsServer,
    PrintPlacardPdfsServer,
    PrintPDCsServer,
    OrgPersonsBulkSendBehavorialAssessmentServer,
    RecentBehavioralAssessmentServer,
    SimulatedBehavioralAssessmentServer,
    CreateBARequestPayload,
    BehavioralAssessmentResponseCto,
    BehavioralAssessmentCreationCto
} from '../core-data-model/AssessmentsApi.serverinterface';
import { BehavioralAssessment } from '../core-data-model/BehavioralAssessment.model';
import { BaPerson } from '../core-data-model/BehavioralAssessmentPerson.model';
import { BehavioralReportConfiguration } from '../core-data-model/BehavioralReportConfiguration.model';
import { CognitiveAssessment } from '../core-data-model/CognitiveAssessment.model';
import { GuidebookServer } from '../core-data-model/Guidebook.interface';
import { AccountStatus } from '../core-data-model/InitData.model';
import { PatternInsights } from '../core-data-model/PatternInsights.interface';
import { Person, PersonUpdate } from '../core-data-model/Person.model';
import {
    CognitiveAssessmentDetailsServer,
    BehavioralAssessmentDetailsServer
} from '../core-data-model/PersonApi.serverinterface';
import { ScheduledAssessmentDetails } from '../core-data-model/ScheduledAssessmentDetails.model';
import { AbstractReportReceiver } from '../core-services/AbstractReport';
import { AssessmentsTransformers } from '../core-services/AssessmentsApi.datatransform';
import { BehavioralAssessmentsQueryGeneratorService } from '../core-services/BehavioralAssessmentsQueryGenerator.service';
import { CognitiveAssessmentsQueryGeneratorService } from '../core-services/CognitiveAssessmentsQueryGenerator.service';
import { SearchStateFactory } from '../core-services/SearchStateFactory';
import { Util } from '../core-utilities/Util';
import { BehavioralAssessmentFactory } from '../object-factory/BehavioralAssessment.factory';
import { HttpHeaderContentType } from '../shared/Enums';
import { ApiService } from '../system-services/Api.service';
import { FeatureFlagsService } from '../system-services/FeatureFlags.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';

@Injectable({
    providedIn: 'root'
})
export class AssessmentsApi {
    private behavioralAssessmentsCache = new Cache<
        BehavioralAssessment[],
        string
    >('behavioralAssessmentsCache', (userId: string) => userId);

    private cognitiveAssessmentsCache = new Cache<
        CognitiveAssessment[],
        string
    >('cognitiveAssessmentsCache', (userId: string) => userId);

    private placardUrlCache = new Cache<BlobPart, string>(
        'placardUrlCache',
        (assessmentId: string) => assessmentId
    );

    private pdcUrlCache = new Cache<BlobPart, string>(
        'pdcUrlCache',
        (assessmentId: string) => assessmentId
    );

    private cognitiveAssessmentsUrlCache = new Cache<BlobPart, string>(
        'cognitiveAssessmentsUrlCache',
        (assessmentId: string) => assessmentId
    );

    private patternInsightsCache = new Cache<any, string>(
        'patternInsightsCache',
        (assessmentId: string) => assessmentId
    );
    private fullPatternImageCache = new Cache<any, string>(
        'fullPatternImageCache',
        (assessmentId: string) => assessmentId
    );

    private readonly baManagementUrlBase = '/api/v5/behavioralassessment';

    constructor(
        private api: ApiService,
        private baFactory: BehavioralAssessmentFactory,
        private transformer: AssessmentsTransformers,
        private userProfile: UserProfileSettingsService,
        private cognitiveAssessmentsODataQueryGenerator: CognitiveAssessmentsQueryGeneratorService,
        private behavioralAssessmentsODataQueryGenerator: BehavioralAssessmentsQueryGeneratorService,
        private util: Util,
        private searchStateFactory: SearchStateFactory,
        private TR: TranslationService,
        private features: FeatureFlagsService,
        private initData: InitDataService
    ) {}

    /**
     * Call Classic endpoint to retrieve BA data based on assessmentId
     * @deprecated Use {@link getBehavioralAssessmentV2} which calls V5 BA API endpoint
     */
    @cacheable(
        function (this: AssessmentsApi) {
            return this.behavioralAssessmentsCache;
        },
        (assessmentId: string) => assessmentId,
        null,
        null,
        // We just need to cache doubled-up calls on the person page
        1000
    )
    getBehavioralAssessment(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BehavioralAssessment> {
        return this.api
            .get<CompleteBehavioralAssessmentServer>(
                `/api/v1/companies/${companyId}/behavioralassessments/${assessmentId}`
            )
            .then(([response]) =>
                this.transformer.behavioralAssessmentTransform(response)
            );
    }

    getAllAssessmentsForPerson(
        userId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<{
        behavioral: BehavioralAssessment[];
        cognitive: CognitiveAssessment[];
    }> {
        return this.api
            .get<GetAllAssessmentsServer>(
                `/api/v1/companies/${companyId}/assessments/dates/${userId}?surveyUserId=${userId}`
            )
            .then(([response]) => {
                const cognitiveAssessments: CognitiveAssessment[] = [];
                const behavioralAssessments: BehavioralAssessment[] = [];
                response.forEach((item) => {
                    if (item.isCognitiveAssessment) {
                        cognitiveAssessments.push(
                            new CognitiveAssessment({
                                completedDate: this.util.newDate(
                                    item.surveySubmittedDate
                                )
                            })
                        );
                    } else {
                        behavioralAssessments.push(
                            this.baFactory.new_BehavioralAssessment({
                                completedDate: this.util.newDate(
                                    item.surveySubmittedDate
                                )
                            })
                        );
                    }
                });
                return {
                    behavioral: behavioralAssessments,
                    cognitive: cognitiveAssessments
                };
            });
    }

    private getAllBehavioralAssessmentsForPersonServer(
        userId: string,
        _clearCache: boolean,
        companyId: string
    ) {
        let queryState = this.searchStateFactory.newSearchState();
        queryState = queryState.addSort(
            SortType.RecentlyCompleted,
            SortDirection.Descending
        );

        const oDataQuery =
            this.behavioralAssessmentsODataQueryGenerator.createODataString(
                queryState
            );
        return this.api.get<BehavioralAssessmentServer[]>(
            `/api/v2/companies/${companyId}/persons/${userId}/behavioralassessments?${oDataQuery}`
        );
    }

    getRecentCompletedBehavioralAssessments(
        completedCardAdministeredByMe: boolean
    ): Promise<Person[]> {
        return this.api
            .get<RecentBehavioralAssessmentServer>(
                `/api/v4/companies/${
                    this.userProfile.currentCompany
                }/behavioralassessments/completed?administeredByAll=${!completedCardAdministeredByMe}`
            )
            .then(([data]) =>
                data.map((item) =>
                    this.transformer.recentBehavioralAssessmentTransform(item)
                )
            );
    }

    getRecentPendingBehavioralAssessments(
        pendingCardAdministeredByMe: boolean
    ): Promise<Person[]> {
        return this.api
            .get<RecentBehavioralAssessmentServer>(
                `/api/v4/companies/${
                    this.userProfile.currentCompany
                }/behavioralassessments/pending?administeredByAll=${!pendingCardAdministeredByMe}`
            )
            .then(([data]) =>
                data.map((item) =>
                    this.transformer.recentBehavioralAssessmentTransform(item)
                )
            );
    }

    getAllBehavioralAssessmentsForPerson(
        userId: string,
        clearCache = false,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BehavioralAssessment[]> {
        return this.getAllBehavioralAssessmentsForPersonServer(
            userId,
            clearCache,
            companyId
        ).then(([response]) =>
            response.map((assessment) =>
                this.transformer.processBehavioralAssessment(assessment)
            )
        );
    }

    @cacheable(
        function (this: AssessmentsApi) {
            return this.cognitiveAssessmentsCache;
        },
        (userId: string) => userId,
        (_userId: string, clearCache: boolean) => clearCache,
        null
    )
    private getAllCognitiveAssessmentsForUserServer(
        userId: string,
        _clearCache: boolean,
        companyId: string
    ) {
        let queryState = this.searchStateFactory.newSearchState();
        queryState = queryState.addSort(
            SortType.CognitiveScore,
            SortDirection.Descending
        );

        const oDataQuery =
            this.cognitiveAssessmentsODataQueryGenerator.createODataString(
                queryState
            );
        return this.api.get<CognitiveAssessmentServer[]>(
            `/api/v1/companies/${companyId}/persons/${userId}/cognitiveAssessments?${oDataQuery}`
        );
    }

    getAllCognitiveAssessmentsForPerson(
        userId: string,
        clearCache = false,
        companyId = this.userProfile.currentCompany
    ): Promise<CognitiveAssessment[]> {
        return this.getAllCognitiveAssessmentsForUserServer(
            userId,
            clearCache,
            companyId
        ).then(([response]) =>
            response.map((assessment) =>
                this.transformer.processCognitiveAssessment(assessment)
            )
        );
    }

    /**
     * Gets the BA for the given SurveyUser. BA API selects the earliest BA score to return.
     * Replaces {@link getBehavioralAssessment} which calls Classic
     */
    async getBehavioralAssessmentV2(
        surveyUserGuid: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<Person | null> {
        const url = `/api/v5/behavioralassessment/companies/${companyId}/${surveyUserGuid}`;

        const [result] =
            await this.api.get<CompleteBehavioralAssessmentServer>(url);
        if (!result) return null;
        return this.transformer.transformBehavioralAssessmentServerToPerson(
            result
        );
    }

    /**
     * @deprecated Use {@link createSurveyUserAndSendBehavioralAssessment} which calls V5 BA API endpoint
     */
    sendBehavioralAssessment(
        scheduledAssessmentDetails: ScheduledAssessmentDetails,
        invitiationSource: InvitationSource,
        skipNotifyAssessment = false,
        sendCognitiveAfterBehavioralIsComplete = false,
        skipNotifyAnalystCompletionEmail = false,
        expirationDateOverride?: Date,
        autoSendBehavioralOnCompletion = false,
        companyId = this.userProfile.currentCompany
    ) {
        const body = this.transformer.sendAssessmentDataTransformation(
            scheduledAssessmentDetails,
            false,
            invitiationSource,
            skipNotifyAnalystCompletionEmail,
            expirationDateOverride,
            autoSendBehavioralOnCompletion,
            sendCognitiveAfterBehavioralIsComplete,
            skipNotifyAssessment
        );
        const flagForNewSurveySite = this.isUsingNewSurveySite
            ? '?directToNewSurveySite=true'
            : '';
        return this.api
            .post<
                any[],
                BehavioralAssessmentDetailsServer[]
            >(`/api/v1/companies/${companyId}/behavioralassessments/synchronousbatch${flagForNewSurveySite}`, body)
            .then((wrappedResponse) => {
                // Lazy but works
                this.behavioralAssessmentsCache.clearAll();

                return wrappedResponse;
            });
    }

    /**
     * This function is used to create a survey user and send a behavioral assessment.
     * If the SurveyUser exists then it just sends the behavioral assessment.
     */
    async createSurveyUserAndSendBehavioralAssessment(
        person: PersonUpdate,
        invitationSource: InvitationSource,
        sendBAReportToAssessmentTaker: boolean,
        companyGuid: string = this.userProfile.currentCompany
    ): Promise<Person> {
        const url = `${this.baManagementUrlBase}/companies/${companyGuid}`;
        const payload = this.transformer.transformCreateBARequestPayload(
            person,
            invitationSource,
            sendBAReportToAssessmentTaker,
            companyGuid
        );
        const [result] = await this.api.post<
            CreateBARequestPayload,
            BehavioralAssessmentDetailsServer
        >(url, payload);
        return this.transformer.transformPersonFromPost(result, person);
    }

    /**
     * This function is used to create a survey user and generate a behavioral assessment.
     * If the SurveyUser exists then it just generates the behavioral assessment.
     * Used for taking Self Assessment as it needs the AssessmentId which will help to generate Assessment Link
     */
    async createSurveyUserAndGenerateBehavioralAssessment(
        invitationSource: InvitationSource,
        folderId: string,
        companyGuid: string = this.userProfile.currentCompany
    ): Promise<BehavioralAssessmentResponseCto> {
        const url = `${this.baManagementUrlBase}/companies/${companyGuid}`;
        const baCreation: BehavioralAssessmentCreationCto = {
            firstName: this.initData.user.firstName,
            lastName: this.initData.user.lastName,
            email: this.initData.user.email,
            folderGuid: folderId,
            personType: SurveyUserTypes.Employee.toString(),
            notifyAssessmentAvailableUsingEmail: 0,
            autoSendPIReportToAssessmentTaker: 1,
            invitationSource: invitationSource
        };
        const [result] = await this.api.post<
            BehavioralAssessmentCreationCto,
            BehavioralAssessmentResponseCto
        >(url, baCreation);
        return result;
    }

    /**
     * This function is used to generate a behavioral assessment.
     * Used for taking Self Assessment as it needs the AssessmentId which will help to generate Assessment Link
     */
    async GenerateBehavioralAssessmentForAssessmentTaker(
        invitationSource: InvitationSource,
        folderId: string,
        companyGuid: string = this.initData.user.companyId
    ): Promise<BehavioralAssessmentResponseCto> {
        const url = `${this.baManagementUrlBase}/companies/${companyGuid}/assessmenttaker`;
        const baCreation: BehavioralAssessmentCreationCto = {
            firstName: this.initData.user.firstName,
            lastName: this.initData.user.lastName,
            email: this.initData.user.email,
            folderGuid: folderId,
            assessmentUserGuid: this.initData.user.surveyUserId,
            personType: SurveyUserTypes.Employee.toString(),
            notifyAssessmentAvailableUsingEmail: 0,
            autoSendPIReportToAssessmentTaker: 1,
            invitationSource: invitationSource
        };
        const [result] = await this.api.post<
            BehavioralAssessmentCreationCto,
            BehavioralAssessmentResponseCto
        >(url, baCreation);
        return result;
    }

    // setting up to send assessments from free to new survey site
    get isUsingNewSurveySite() {
        return (
            this.features.get('directBehavioralToNewSurveySite') ||
            this.initData.account.accountStatus === AccountStatus.HireTrial
        );
    }

    /**
     * @deprecated Use {@link updateBehavioralAssessmentAndResend} which calls V5 BA API endpoint
     */
    updateAssessmentAndResend(
        assessmentId: string,
        customSubject?: string,
        customMessage?: string,
        companyId = this.userProfile.currentCompany
    ): Promise<any> {
        return this.api
            .patch(
                `/api/v1/companies/${companyId}/behavioralassessments/${assessmentId}`,
                {
                    resendAssessment: true,
                    subject: customSubject,
                    message: customMessage
                }
            )
            .then((wrappedResponse) => {
                // Lazy but works
                this.behavioralAssessmentsCache.clearAll();
                return wrappedResponse;
            });
    }

    async updateBehavioralAssessmentAndResend(
        surveySessionId: string
    ): Promise<void> {
        const url = `${this.baManagementUrlBase}/companies/${this.userProfile.currentCompany}/${surveySessionId}/resend`;
        await this.api.patch(url, {});
    }

    sendCognitiveAssessment(
        scheduledAssessmentDetails: ScheduledAssessmentDetails,
        companyId = this.userProfile.currentCompany
    ) {
        const body = this.transformer.sendAssessmentDataTransformation(
            scheduledAssessmentDetails,
            true,
            InvitationSource.Unknown
        );
        return this.api
            .post<
                any[],
                CognitiveAssessmentDetailsServer[]
            >(`/api/v2/companies/${companyId}/cognitiveassessments/synchronousbatch`, body)
            .then((wrappedResponse) => {
                // Lazy but works
                this.cognitiveAssessmentsCache.clearAll();

                return wrappedResponse;
            });
    }

    deleteAssessment(
        assessmentId: string,
        assessmentType: AssessmentType,
        userId: string,
        companyId = this.userProfile.currentCompany
    ) {
        const version =
            assessmentType === AssessmentType.Behavioral ? 'v1' : 'v2';
        const type =
            assessmentType === AssessmentType.Behavioral
                ? 'behavioralassessments'
                : 'cognitiveassessments';
        return this.api
            .delete(
                `/api/${version}/companies/${companyId}/${type}/${assessmentId}`
            )
            .then(() => {
                if (assessmentType === AssessmentType.Behavioral) {
                    this.behavioralAssessmentsCache.clear(userId);
                } else if (assessmentType === AssessmentType.Cognitive) {
                    this.cognitiveAssessmentsCache.clear(userId);
                }
            });
    }

    @cacheable(
        function (this: AssessmentsApi) {
            return this.placardUrlCache;
        },
        (assessmentId: string) => assessmentId,
        null,
        new BlobSerializer(),
        sevenDaysMilli
    )
    private getPlacardPdfRaw(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany,
        contentType: HttpHeaderContentType
    ) {
        // NOTE: If gender is required this can be tweaked
        return this.api.getResourceUrl(
            `/api/v1/companies/${companyId}/behavioralassessments/${assessmentId}/placards/0?format=PDF`,
            contentType
        );
    }

    getPlacardPdf(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BlobPart> {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.processResourceUrl(
            this.getPlacardPdfRaw(assessmentId, companyId, contentType),
            contentType
        );
    }

    getPlacardPdfBulk(
        recipients: AbstractReportReceiver[],
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ) {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.postResourceUrl<PrintPlacardPdfsServer>(
            `/api/v1/companies/${companyId}/persons/PrintPlacards`,
            // TODO: move this to a transform
            {
                reportLanguage: languageLocale,
                personsToReportOn: recipients.map((r) => r.id)
            },
            contentType
        );
    }

    @cacheable(
        function (this: AssessmentsApi) {
            return this.pdcUrlCache;
        },
        (assessmentId: string) => assessmentId,
        null,
        new BlobSerializer(),
        sevenDaysMilli
    )
    private getPdcPdfRaw(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany,
        contentType: HttpHeaderContentType
    ) {
        return this.api.getResourceUrl(
            `/api/v1/companies/${companyId}/behavioralassessments/${assessmentId}/reports/PersonDevelopmentChart` +
                '?format=PDF' +
                // NOTE: If gender is required this can be tweaked
                '&gender=Unknown',
            contentType
        );
    }

    getPdcPdf(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BlobPart> {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.processResourceUrl(
            this.getPdcPdfRaw(assessmentId, companyId, contentType),
            contentType
        );
    }

    getPdcPdfBulk(
        recipients: AbstractReportReceiver[],
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ) {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.postResourceUrl<PrintPDCsServer>(
            `/api/v1/companies/${companyId}/persons/PrintPDCs`,
            // TODO: move this to a transform
            {
                reportLanguage: languageLocale,
                personsToReportOn: recipients.map((r) => r.id)
            },
            contentType
        );
    }

    // NOTE: DO NOT CACHE Behavioral Reports since the Report Configs are reseted every single time as per expected behavior.
    getBehavioralReportPdf(
        assessmentId: string,
        reportConfig: BehavioralReportConfiguration,
        gender: LegacyGender,
        companyId: string = this.userProfile.currentCompany
    ) {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.processResourceUrl(
            this.api.getResourceUrl(
                `/api/v1/companies/${companyId}/behavioralassessments/${assessmentId}/reports/PersonDetailReport` +
                    '?format=PDF' +
                    `&gender=${LegacyGender[gender]}` +
                    `&languageLocale=${reportConfig.languageLocale}` +
                    `&isManagementStyle=${reportConfig.isManagement}` +
                    `&isInfluencingStyle=${reportConfig.isInfluencing}` +
                    `&isSellingStyle=${reportConfig.isSelling}`,
                contentType
            ),
            contentType
        );
    }

    getBehavioralReportPdfBulk(
        recipients: AbstractReportReceiver[],
        reportConfig: BehavioralReportConfiguration,
        companyId: string = this.userProfile.currentCompany
    ) {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.postResourceUrl<PrintBehavioralReportsServer>(
            `/api/v1/companies/${companyId}/persons/PrintBehavioralReports`,
            // TODO: move this to a transform
            {
                reportLanguage: reportConfig.languageLocale,
                personsToReportOn: recipients.map((r) => ({
                    personId: r.id,
                    gender: LegacyGender[r.gender]
                })),
                isManagementStyle: reportConfig.isManagement,
                isSellingStyle: reportConfig.isSelling,
                isInfluencingStyle: reportConfig.isInfluencing
            },
            contentType
        );
    }

    @cacheable(
        function (this: AssessmentsApi) {
            return this.cognitiveAssessmentsUrlCache;
        },
        (assessmentId: string, languageLocale: string) =>
            assessmentId + languageLocale,
        null,
        new BlobSerializer()
    )
    private getCognitiveReportPdfRaw(
        assessmentId: string,
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany,
        contentType: HttpHeaderContentType
    ) {
        return this.api.getResourceUrl(
            `/api/v2/companies/${companyId}/cognitiveassessments/${assessmentId}/reports/0?languageLocale=${languageLocale}`,
            contentType
        );
    }

    getCognitiveReportPdf(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany,
        languageLocale: string
    ): Promise<BlobPart> {
        const contentType = HttpHeaderContentType.PDF;
        return this.api.processResourceUrl(
            this.getCognitiveReportPdfRaw(
                assessmentId,
                languageLocale,
                companyId,
                contentType
            ),
            contentType
        );
    }

    static getBehavioralAssessmentLoggingInfo = (
        retVal: [BehavioralAssessmentDetailsServer[], any]
    ): any => {
        const result: any[] = [];

        retVal[0].forEach((element) => {
            const trackObject = {
                assessmentId: element.assessmentId,
                surveyUserId: element.assessmentUserId,
                email: element.email
            };
            result.push(trackObject);
        });
        return result;
    };

    static getCognitiveAssessmentLoggingInfo = (
        retVal: [CognitiveAssessmentDetailsServer[], any]
    ): any => {
        const result: any[] = [];

        retVal[0].forEach((element) => {
            const trackObject: any = {
                assessmentId: element.assessmentId,
                surveyUserId: element.surveyUser.assessmentUserId,
                email: element.surveyUser.email
            };
            result.push(trackObject);
        });
        return result;
    };

    @cacheable(
        function (this: AssessmentsApi) {
            return this.patternInsightsCache;
        },
        (assessmentId: string, languageLocale: string) =>
            assessmentId + languageLocale,
        null
    )
    /**
     * @deprecated This method is deprecated. Please use `getPersonalDetailsServer` to retrieve Behavioral Assessment personal details.
     */
    private getPatternInsightsServer(
        assessmentId: string,
        languageLocale: string = this.TR.currentLocale,
        companyId: string
    ) {
        return this.api.get<GuidebookServer>(
            `/api/v4/companies/${companyId}/persons/${assessmentId}/personSnapshotInfo?languageLocale=${languageLocale}`
        );
    }

    private getPersonalDetailsServer(
        surveyUserId: string,
        companyId: string,
        language = this.TR.currentLocale
    ) {
        return this.api.get<BaPerson>(
            `/api/v5-inspire/companies/${companyId}/common/person/${surveyUserId}?languageLocale=${language}`
        );
    }

    /**
     * @deprecated This method is deprecated. Please use `getPersonalDetails` to retrieve Behavioral Assessment personal details.
     */
    async getPatternInsights(
        personId: string,
        companyId: string = this.userProfile.currentCompany,
        languageLocale: string = this.TR.currentLocale
    ): Promise<PatternInsights> {
        const [server] = await this.getPatternInsightsServer(
            personId,
            languageLocale,
            companyId
        );
        return this.transformer.transformPatternInsights(server);
    }

    async getPersonalDetails(personId: string): Promise<BaPerson> {
        const companyId =
            personId === this.initData.user.surveyUserId
                ? this.initData.user.companyId
                : this.userProfile.currentCompany;
        const [server] = await this.getPersonalDetailsServer(
            personId,
            companyId
        );
        return server;
    }

    @cacheable(
        function (this: AssessmentsApi) {
            return this.fullPatternImageCache;
        },
        (assessmentId: string) => assessmentId,
        null,
        new BlobSerializer()
    )
    getFullPatternImageBlob(
        assessmentId: string,
        companyId: string = this.userProfile.currentCompany,
        languageLocale: string = this.TR.currentLocale
    ) {
        const contentType = HttpHeaderContentType.PNG;
        return this.api.getResourceUrl(
            `/api/v1/companies/${companyId}/behavioralassessments/${assessmentId}/patterns/Complete` +
                '?format=PNG' +
                `&languageLocale=${languageLocale}`,
            contentType
        );
    }

    getFullPatternImage(
        patternParam: string,
        languageLocale: string = this.TR.currentLocale
    ) {
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/reportImage?reportParam=${patternParam}&reportImageType=behavioralfullpattern&languageLocale=${languageLocale}`,
            HttpHeaderContentType.PDF,
            false,
            null
        );
    }

    getBAChecklistPdf(languageLocale: string = this.TR.currentLocale) {
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/bachecklistpdf?languageLocale=${languageLocale}`,
            HttpHeaderContentType.PDF,
            false,
            null
        );
    }

    uploadBulkAssessmentCSV(
        file: File,
        folderId: string,
        jobId: string,
        emailSubject: string,
        emailMessage: string,
        languageLocale: string,
        bulkUploadAssessmentType: BulkUploadAssessmentType,
        personType: SurveyUserTypes,
        sendAnalystCompletionEmail: boolean,
        companyId: string = this.userProfile.currentCompany
    ): Promise<boolean> {
        const formData = new FormData();
        formData.append('fileData', file, file.name);
        formData.append('folderId', folderId);
        formData.append('jobId', jobId);
        formData.append('emailSubject', emailSubject);
        formData.append('emailMessage', emailMessage);
        formData.append('languageLocale', languageLocale);
        formData.append(
            'assessmentType',
            BulkUploadAssessmentType[bulkUploadAssessmentType]
        );
        formData.append('personType', SurveyUserTypes[personType]);
        formData.append(
            'sendAnalystCompletionEmail',
            sendAnalystCompletionEmail.toString()
        );
        formData.append(
            'isUsingNewSurveySite',
            this.features.get('directBehavioralToNewSurveySite').toString()
        );

        return this.api
            .postFile(
                `/api/v4/companies/${companyId}/assessments/bulkCreateWithCSV`,
                formData
            )
            .then(([server]) => server)
            .catch(() => false);
    }

    sendBAToAllOrgPeopleMissingBA(
        orgPersonsBulkSendBA?: OrgPersonsBulkSendBehavorialAssessmentServer,
        companyId: string = this.userProfile.currentCompany,
        languageLocale: string = this.TR.currentLocale
    ) {
        return this.api.post(
            `/api/v5-diagnose/companies/${companyId}/assessments/bulkOrgPersonSendBAInvite?languageLocale=${languageLocale}`,
            orgPersonsBulkSendBA ? orgPersonsBulkSendBA : null
        );
    }

    getSimulatedPatternforSelfConceptCoaching(
        patternIdForSelf: string,
        patternIdForSelfConcept: string,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api
            .get<SimulatedBehavioralAssessmentServer>(
                `/api/v3/companies/${companyId}/behavioralassessments/simulatedpattern?selfPatternId=${patternIdForSelf}&selfConceptPatternId=${patternIdForSelfConcept}`
            )
            .then(([response]) =>
                this.transformer.transformSimulatedBehavioralPattern(response)
            );
    }
}
